import { schemes, zSchema } from "../../schema-validation";
import { ETxnSpeed } from "../../transaction-speeds";
import { EDisbursementMethod } from "../../workers-shared";
import { EContractorPaymentStatus } from "../EContractorPaymentStatus";
import { EContractorPaymentType } from "../EContractorPaymentType";

// Base schema for full contractor payment (includes all DB fields)
export const contractorPaymentSchema = zSchema.object({
  _id: schemes.mongoObjectId(),
  /**
   * @deprecated Use `totals.gross_amount` and `totals.net_amount` instead. This field is maintained for backward compatibility.
   * For payments with `has_payment_items: true`, use `totals.gross_amount` for gross amount or `totals.net_amount` for net amount.
   */
  amount: zSchema.number().nullish(),
  approval_required: zSchema.boolean(),
  approved: zSchema.boolean(),
  companyID: schemes.companyID(),
  contractorID: schemes.mongoObjectId(),
  createdAt: zSchema.string(),
  customerAccountID: schemes.uuid().nullish(),
  disbursement: zSchema.object({
    history: zSchema.array(
      zSchema.object({
        status: zSchema.string(),
      })
    ),
    method: zSchema.nativeEnum(EDisbursementMethod),
  }),
  first_name: zSchema.string(),
  group_id: schemes.uuid().optional(),
  has_payment_items: zSchema.boolean(),
  last_name: zSchema.string(),
  pay_date: schemes.isoDate(),
  speed: zSchema.nativeEnum(ETxnSpeed).optional(),
  status: zSchema.nativeEnum(EContractorPaymentStatus),
  triggered: zSchema.boolean(),
  triggered_at: zSchema.string().optional(),
  type: zSchema.nativeEnum(EContractorPaymentType),
  updatedAt: zSchema.string(),
  metadata: zSchema.record(zSchema.string(), zSchema.any()),
  totals: zSchema.object({
    company_cash_requirement: zSchema.number().nullish(),
    company_debit: zSchema.number().nullish(),
    gross_amount: zSchema.number(),
    net_amount: zSchema.number(),
    deductions: zSchema.number(),
  }),
});

// Response schema for API responses (matches adapter output)
// This is the single source of truth for contractor payment API responses
export const contractorPaymentResponseSchema = zSchema.object({
  contractorPaymentID: schemes.mongoObjectId(),
  /**
   * @deprecated Use `totals.gross_amount` and `totals.net_amount` instead. This field is maintained for backward compatibility.
   * For payments with `has_payment_items: true`, use `totals.gross_amount` for gross amount or `totals.net_amount` for net amount.
   */
  amount: zSchema.number().nullish(),
  approval_required: zSchema.boolean(),
  approved: zSchema.boolean(),
  companyID: schemes.companyID(),
  contractorID: schemes.mongoObjectId(),
  customerAccountID: schemes.uuid().nullish(),
  disbursement: zSchema.object({
    history: zSchema.array(
      zSchema.object({
        date: zSchema.string(),
        status: zSchema.string(),
      })
    ),
    method: zSchema.nativeEnum(EDisbursementMethod),
    status: zSchema
      .enum([
        "pending",
        "settled",
        "canceled",
        "returned",
        "net_pay_void",
        "refunded",
      ])
      .nullish(),
  }),
  first_name: zSchema.string(),
  middle_name: zSchema.string().nullish(),
  last_name: zSchema.string(),
  has_payment_items: zSchema.boolean(),
  pay_date: schemes.isoDate(),
  speed: zSchema.nativeEnum(ETxnSpeed).optional(),
  status: zSchema.nativeEnum(EContractorPaymentStatus),
  type: zSchema.nativeEnum(EContractorPaymentType).optional(),
  metadata: zSchema.record(zSchema.string(), zSchema.any()),
  totals: zSchema.object({
    company_cash_requirement: zSchema.number().nullish(),
    company_debit: zSchema.number().nullish(),
    gross_amount: zSchema.number(),
    net_amount: zSchema.number(),
    deductions: zSchema.number(),
  }),
});

// Derived TypeScript type from the response schema
export type IContractorPaymentResponse = zSchema.infer<
  typeof contractorPaymentResponseSchema
>;
